import { useEffect, useState, useRef } from 'react';
import type { LoadingScreenConfig } from '../types';

// ─── CSS animations injected once ─────────────────────────────────────────────

const STYLE = `
@keyframes ls-fade-in  { from { opacity:0 } to { opacity:1 } }
@keyframes ls-fade-out { from { opacity:1 } to { opacity:0 } }
@keyframes ls-slide-up { from { transform:translateY(0) } to { transform:translateY(-100%) } }
@keyframes ls-slide-left  { from { transform:translateX(0) } to { transform:translateX(-100%) } }
@keyframes ls-slide-right { from { transform:translateX(0) } to { transform:translateX(100%) } }
@keyframes ls-letter { 0%{opacity:0;transform:translateY(24px)} 60%{opacity:1;transform:translateY(-3px)} 100%{opacity:1;transform:translateY(0)} }
@keyframes ls-tagline { 0%{opacity:0;letter-spacing:0.4em} 100%{opacity:1;letter-spacing:0.2em} }
@keyframes ls-dot { 0%,80%,100%{transform:scale(0);opacity:0.3} 40%{transform:scale(1);opacity:1} }
@keyframes ls-progress { from{width:0} to{width:100%} }
@keyframes ls-ring { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
@keyframes ls-pulse-ring { 0%{transform:scale(0.8);opacity:1} 100%{transform:scale(2.2);opacity:0} }
@keyframes ls-bar-fill { 0%{transform:scaleX(0)} 100%{transform:scaleX(1)} }
`;

function injectStyle() {
  if (document.getElementById('ls-styles')) return;
  const el = document.createElement('style');
  el.id = 'ls-styles';
  el.textContent = STYLE;
  document.head.appendChild(el);
}

// ─── Default config ────────────────────────────────────────────────────────────

const DEFAULT_CONFIG: LoadingScreenConfig = {
  enabled: true,
  style: 'logo_reveal',
  bg_color: '#0f0f0f',
  text_color: '#ffffff',
  accent_color: '#c9a96e',
  logo_text: 'FRAMED',
  tagline: 'Art, Framed With Intent',
  min_duration: 1200,
};

const LS_CACHE_KEY = 'ls_cfg';

export function getLoadingConfig(settings?: { loading_screen?: Partial<LoadingScreenConfig> }): LoadingScreenConfig {
  const cached = (() => {
    try { return JSON.parse(localStorage.getItem(LS_CACHE_KEY) ?? ''); } catch { return null; }
  })();
  return { ...DEFAULT_CONFIG, ...(cached ?? {}), ...(settings?.loading_screen ?? {}) };
}

export function cacheLoadingConfig(cfg: LoadingScreenConfig) {
  try { localStorage.setItem(LS_CACHE_KEY, JSON.stringify(cfg)); } catch { /* ignore */ }
}

// ─── Style renderers ───────────────────────────────────────────────────────────

function LogoReveal({ cfg, hiding }: { cfg: LoadingScreenConfig; hiding: boolean }) {
  const letters = cfg.logo_text.split('');
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: cfg.bg_color,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      animation: hiding ? `ls-fade-out 0.6s ease forwards` : undefined,
    }}>
      {/* Accent bar */}
      <div style={{
        width: 48, height: 2, background: cfg.accent_color,
        marginBottom: 28,
        animation: 'ls-bar-fill 0.6s ease 0.2s both',
        transformOrigin: 'left',
      }}/>
      {/* Logo letters */}
      <div style={{ display: 'flex', gap: 4 }}>
        {letters.map((ch, i) => (
          <span key={i} style={{
            fontFamily: 'Georgia, serif',
            fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
            fontWeight: 300,
            letterSpacing: '0.18em',
            color: cfg.text_color,
            display: 'inline-block',
            animation: `ls-letter 0.5s cubic-bezier(0.22,1,0.36,1) ${0.3 + i * 0.07}s both`,
          }}>{ch === ' ' ? ' ' : ch}</span>
        ))}
      </div>
      {/* Tagline */}
      {cfg.tagline && (
        <p style={{
          marginTop: 16,
          fontFamily: 'system-ui, sans-serif',
          fontSize: '0.7rem',
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: cfg.accent_color,
          animation: `ls-tagline 0.8s ease ${0.3 + letters.length * 0.07 + 0.2}s both`,
        }}>{cfg.tagline}</p>
      )}
      {/* Bottom accent */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(to right, transparent, ${cfg.accent_color}, transparent)`,
        animation: 'ls-bar-fill 1.2s ease 0.1s both',
        transformOrigin: 'left',
      }}/>
    </div>
  );
}

function Curtain({ cfg, hiding }: { cfg: LoadingScreenConfig; hiding: boolean }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: cfg.bg_color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: hiding ? `ls-slide-up 0.7s cubic-bezier(0.76,0,0.24,1) forwards` : `ls-fade-in 0.3s ease forwards`,
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          fontFamily: 'Georgia, serif',
          fontSize: 'clamp(2rem, 5vw, 3.5rem)',
          fontWeight: 300,
          letterSpacing: '0.25em',
          color: cfg.text_color,
          animation: 'ls-fade-in 0.5s ease 0.1s both',
        }}>{cfg.logo_text}</div>
        {cfg.tagline && (
          <p style={{
            marginTop: 12,
            fontSize: '0.65rem',
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            color: cfg.accent_color,
            animation: 'ls-fade-in 0.5s ease 0.3s both',
          }}>{cfg.tagline}</p>
        )}
      </div>
    </div>
  );
}

function Split({ cfg, hiding }: { cfg: LoadingScreenConfig; hiding: boolean }) {
  return (
    <>
      {/* Left panel */}
      <div style={{
        position: 'fixed', inset: 0, right: '50%', zIndex: 9999,
        background: cfg.bg_color,
        animation: hiding ? `ls-slide-left 0.65s cubic-bezier(0.76,0,0.24,1) forwards` : undefined,
      }}/>
      {/* Right panel */}
      <div style={{
        position: 'fixed', inset: 0, left: '50%', zIndex: 9999,
        background: cfg.bg_color,
        display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
        paddingLeft: 24,
        animation: hiding ? `ls-slide-right 0.65s cubic-bezier(0.76,0,0.24,1) forwards` : undefined,
      }}>
        {!hiding && (
          <div>
            <div style={{
              fontFamily: 'Georgia, serif',
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 300,
              color: cfg.text_color,
              letterSpacing: '0.15em',
              animation: 'ls-fade-in 0.5s ease 0.2s both',
            }}>{cfg.logo_text}</div>
            {cfg.tagline && (
              <p style={{
                marginTop: 8,
                fontSize: '0.6rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: cfg.accent_color,
                animation: 'ls-fade-in 0.5s ease 0.4s both',
              }}>{cfg.tagline}</p>
            )}
          </div>
        )}
      </div>
      {/* Vertical line */}
      {!hiding && (
        <div style={{
          position: 'fixed', top: 0, bottom: 0, left: '50%', width: 2,
          background: cfg.accent_color, zIndex: 10000,
          animation: 'ls-fade-in 0.3s ease forwards',
        }}/>
      )}
    </>
  );
}

function Dots({ cfg, hiding }: { cfg: LoadingScreenConfig; hiding: boolean }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: cfg.bg_color,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 24,
      animation: hiding ? `ls-fade-out 0.5s ease forwards` : `ls-fade-in 0.3s ease forwards`,
    }}>
      <div style={{
        fontFamily: 'Georgia, serif',
        fontSize: '1.8rem',
        fontWeight: 300,
        letterSpacing: '0.2em',
        color: cfg.text_color,
        animation: 'ls-fade-in 0.4s ease 0.1s both',
      }}>{cfg.logo_text}</div>
      <div style={{ display: 'flex', gap: 10 }}>
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} style={{
            width: 8, height: 8, borderRadius: '50%',
            background: cfg.accent_color,
            animation: `ls-dot 1.4s ease-in-out ${i * 0.15}s infinite`,
          }}/>
        ))}
      </div>
      {cfg.tagline && (
        <p style={{
          fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase',
          color: cfg.text_color, opacity: 0.4,
          animation: 'ls-fade-in 0.4s ease 0.3s both',
        }}>{cfg.tagline}</p>
      )}
    </div>
  );
}

function Progress({ cfg, hiding }: { cfg: LoadingScreenConfig; hiding: boolean }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: cfg.bg_color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: hiding ? `ls-fade-out 0.5s ease forwards` : `ls-fade-in 0.3s ease forwards`,
    }}>
      {/* Progress bar at bottom */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: `${cfg.accent_color}22` }}>
        <div style={{
          height: '100%', background: cfg.accent_color,
          transformOrigin: 'left',
          animation: `ls-bar-fill ${cfg.min_duration}ms cubic-bezier(0.4,0,0.2,1) forwards`,
        }}/>
      </div>
      <div style={{ textAlign: 'center' }}>
        {/* Ring spinner */}
        <div style={{
          width: 56, height: 56,
          border: `2px solid ${cfg.accent_color}30`,
          borderTop: `2px solid ${cfg.accent_color}`,
          borderRadius: '50%',
          animation: 'ls-ring 0.9s linear infinite',
          margin: '0 auto 20px',
        }}/>
        <div style={{
          fontFamily: 'Georgia, serif', fontSize: '1.5rem', fontWeight: 300,
          letterSpacing: '0.2em', color: cfg.text_color,
          animation: 'ls-fade-in 0.4s ease 0.1s both',
        }}>{cfg.logo_text}</div>
        {cfg.tagline && (
          <p style={{
            marginTop: 8, fontSize: '0.6rem', letterSpacing: '0.18em',
            textTransform: 'uppercase', color: cfg.accent_color,
            animation: 'ls-fade-in 0.4s ease 0.3s both',
          }}>{cfg.tagline}</p>
        )}
      </div>
    </div>
  );
}

function Minimal({ cfg, hiding }: { cfg: LoadingScreenConfig; hiding: boolean }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: cfg.bg_color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: hiding ? `ls-fade-out 0.5s ease forwards` : `ls-fade-in 0.3s ease forwards`,
    }}>
      {/* Pulse ring */}
      <div style={{ position: 'relative', width: 64, height: 64 }}>
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          border: `2px solid ${cfg.accent_color}`,
          animation: 'ls-pulse-ring 1.4s cubic-bezier(0.215,0.61,0.355,1) infinite',
        }}/>
        <div style={{
          position: 'absolute', inset: 8, borderRadius: '50%',
          background: cfg.accent_color,
        }}/>
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

interface LoadingScreenProps {
  config: LoadingScreenConfig;
  ready: boolean;
}

export function LoadingScreen({ config: cfg, ready }: LoadingScreenProps) {
  const [hiding, setHiding] = useState(false);
  const [gone, setGone] = useState(false);
  const startRef = useRef(Date.now());

  useEffect(() => { injectStyle(); }, []);

  useEffect(() => {
    if (!ready || gone) return;
    const elapsed = Date.now() - startRef.current;
    const wait = Math.max(0, cfg.min_duration - elapsed);
    const t = setTimeout(() => {
      setHiding(true);
      setTimeout(() => setGone(true), 800);
    }, wait);
    return () => clearTimeout(t);
  }, [ready, cfg.min_duration, gone]);

  if (!cfg.enabled || gone) return null;

  const props = { cfg, hiding };

  switch (cfg.style) {
    case 'curtain':   return <Curtain {...props}/>;
    case 'split':     return <Split {...props}/>;
    case 'dots':      return <Dots {...props}/>;
    case 'progress':  return <Progress {...props}/>;
    case 'minimal':   return <Minimal {...props}/>;
    default:          return <LogoReveal {...props}/>;
  }
}
